/**
 * Network Formatting Utilities
 * ============================
 * Helper functions for displaying network speeds with color coding.
 */

/**
 * Speed color scheme based on link speed:
 * - Amber: 10/100 Mbps (slow)
 * - Green: 1 GbE
 * - Blue: 2.5 GbE
 * - Purple: 5 GbE
 * - Red: 10 GbE and above (flaming red for fast!)
 */

export type SpeedColorClass = {
  text: string
  textDimmed: string
}

/**
 * Get Tailwind CSS classes for a network speed string.
 * Returns both active and dimmed versions for connected/disconnected states.
 *
 * @param speed - Speed string like "1GbE", "2.5GbE", "100Mbps", etc.
 * @returns Object with text classes for active and dimmed states
 */
export function getSpeedColorClasses(speed: string | null | undefined): SpeedColorClass {
  if (!speed || speed === '-') {
    return {
      text: 'text-gray-900 dark:text-white',
      textDimmed: 'text-gray-500 dark:text-gray-400',
    }
  }

  const speedLower = speed.toLowerCase()

  // 10GbE and above - Flaming Red
  if (
    speedLower.includes('10gbe') ||
    speedLower.includes('25gbe') ||
    speedLower.includes('40gbe') ||
    speedLower.includes('100gbe') ||
    speedLower.includes('10g')
  ) {
    return {
      text: 'text-red-600 dark:text-red-500',
      textDimmed: 'text-red-600/50 dark:text-red-500/50',
    }
  }

  // 5GbE - Purple
  if (speedLower.includes('5gbe') || speedLower.includes('5g')) {
    return {
      text: 'text-purple-600 dark:text-purple-400',
      textDimmed: 'text-purple-600/50 dark:text-purple-400/50',
    }
  }

  // 2.5GbE - Sky Blue (clearly blue, not purple)
  if (speedLower.includes('2.5gbe') || speedLower.includes('2.5g')) {
    return {
      text: 'text-sky-500 dark:text-sky-400',
      textDimmed: 'text-sky-500/50 dark:text-sky-400/50',
    }
  }

  // 1GbE - Green
  if (speedLower.includes('1gbe') || speedLower.includes('1g') || speedLower === '1000mbps') {
    return {
      text: 'text-green-600 dark:text-green-400',
      textDimmed: 'text-green-600/50 dark:text-green-400/50',
    }
  }

  // 10/100 Mbps - Amber
  if (speedLower.includes('mbps') || speedLower.includes('100m') || speedLower.includes('10m')) {
    return {
      text: 'text-amber-600 dark:text-amber-400',
      textDimmed: 'text-amber-600/50 dark:text-amber-400/50',
    }
  }

  // Default - no color
  return {
    text: 'text-gray-900 dark:text-white',
    textDimmed: 'text-gray-500 dark:text-gray-400',
  }
}
